import { useState } from 'react';
import {
  AlertOctagon,
  Battery,
  MapPin,
  Navigation,
  Signal,
  Upload,
  Wifi,
} from 'lucide-react';
import { ACTIVE_TRIP } from './mockMobileData';

function StatusBar() {
  return (
    <div className="flex items-center justify-between px-5 pt-3 text-[10px] font-medium text-logi-muted">
      <span>2:18 PM</span>
      <div className="absolute left-1/2 top-2 h-6 w-28 -translate-x-1/2 rounded-full bg-black/80" />
      <div className="flex items-center gap-1">
        <Signal className="h-3 w-3" />
        <Wifi className="h-3 w-3" />
        <Battery className="h-3 w-3" />
        <span>87%</span>
      </div>
    </div>
  );
}

export default function MobileSimulator({ trip = ACTIVE_TRIP }) {
  const [podUploaded, setPodUploaded] = useState(false);
  const [incidentReported, setIncidentReported] = useState(false);

  const hrs = Math.floor(trip.navigation.remainingMinutes / 60);
  const mins = trip.navigation.remainingMinutes % 60;

  return (
    <div className="mx-auto w-full max-w-[320px]">
      <p className="mb-3 text-center text-[10px] font-semibold uppercase tracking-widest text-logi-text0">
        Module 5 · Driver Mobile View
      </p>

      {/* Phone frame */}
      <div className="relative rounded-[2.5rem] border-[3px] border-logi-grid bg-white p-2 shadow-2xl shadow-logi-md ring-1 ring-logi-grid">
        <div className="overflow-hidden rounded-[2rem] bg-gradient-to-b from-logi-silver to-white">
          <StatusBar />

          <div className="space-y-4 px-4 pb-6 pt-8">
            {/* App header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-logi-text0">
                  OGTS Driver
                </p>
                <p className="text-sm font-bold text-logi-text">{trip.driverName}</p>
              </div>
              <span className="font-mono text-[10px] text-logi-accent">{trip.vehicleNumber}</span>
            </div>

            {/* Active trip card */}
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 ring-1 ring-emerald-500/20">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
                </span>
                <span className="text-xs font-bold uppercase tracking-wide text-emerald-700">
                  {trip.status}
                </span>
              </div>
              <p className="mt-2 text-[10px] text-logi-text0">{trip.tripId} · {trip.client}</p>
              <p className="mt-1 text-xs font-semibold text-amber-700/90">{trip.cargo}</p>

              <div className="mt-4 space-y-3">
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-emerald-400/30" />
                    <div className="my-1 w-0.5 flex-1 bg-gradient-to-b from-emerald-500/50 to-logi-silver0/50" />
                    <div className="h-2.5 w-2.5 rounded-full bg-blue-400 ring-2 ring-blue-400/30" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <p className="text-[10px] uppercase text-logi-text0">Pickup</p>
                      <p className="text-sm font-medium text-logi-text">{trip.pickup.label}</p>
                      <p className="text-[10px] text-logi-text0">
                        ✓ {trip.pickup.completedAt}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-logi-text0">Delivery</p>
                      <p className="text-sm font-medium text-logi-text">{trip.delivery.label}</p>
                      <p className="text-[10px] text-blue-700">ETA {trip.delivery.eta}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation box */}
            <div className="overflow-hidden rounded-2xl border border-logi-grid bg-white">
              <div className="relative h-28 bg-gradient-to-br from-blue-100 via-logi-silver to-logi-silver">
                <div className="absolute inset-0 opacity-30">
                  <div className="h-full w-full bg-[linear-gradient(45deg,transparent_40%,rgba(59,130,246,0.15)_50%,transparent_60%)]" />
                </div>
                <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
                  <Navigation className="h-8 w-8 text-blue-700" strokeWidth={1.5} />
                  <div className="text-right">
                    <p className="font-mono text-lg font-bold text-logi-text">
                      {hrs}h {mins}m
                    </p>
                    <p className="text-[10px] text-logi-muted">{trip.navigation.remainingKm} km left</p>
                  </div>
                </div>
              </div>
              <div className="border-t border-logi-grid px-3 py-2.5">
                <p className="flex items-center gap-1 text-[11px] font-medium text-blue-300">
                  <MapPin className="h-3 w-3" />
                  {trip.navigation.currentRoad}
                </p>
                <p className="mt-0.5 text-[10px] text-logi-text0">{trip.navigation.nextManeuver}</p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setPodUploaded(true)}
                className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition ${
                  podUploaded
                    ? 'bg-emerald-600/20 text-emerald-700 ring-1 ring-emerald-500/30'
                    : 'bg-logi-accent text-white shadow-lg shadow-blue-500/20 hover:bg-cyan-500'
                }`}
              >
                <Upload className="h-4 w-4" />
                {podUploaded ? 'POD Uploaded ✓' : 'Upload POD (Proof of Delivery)'}
              </button>
              <button
                type="button"
                onClick={() => setIncidentReported(true)}
                className={`flex w-full items-center justify-center gap-2 rounded-xl border py-3 text-sm font-semibold transition ${
                  incidentReported
                    ? 'border-red-500/40 bg-red-50 text-red-700'
                    : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-50'
                }`}
              >
                <AlertOctagon className="h-4 w-4" />
                {incidentReported ? 'Incident Reported' : 'Report Incident (Spillage/Accident)'}
              </button>
            </div>

            <p className="text-center text-[9px] text-logi-text0">
              OGTS LogiCore™ Driver App · v1.0 Prototype
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
